import React, { useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import { PictureAsPdf, Visibility, ContentCopy } from '@mui/icons-material';
import { IHandbookDocument } from 'core/interfaces/handbookInterface';
import { getHandbookDocumentUrl, listHandbookDocuments } from 'core/API/handbookDocuments';
import { useToastContext } from 'core/components/Toast/context/ToastContext';
import { PdfViewerDialog } from 'core/components/PdfViewerDialog/PdfViewerDialog';

export const EmployeeDocuments = () => {
  const { showToast } = useToastContext();
  const [documents, setDocuments] = useState<IHandbookDocument[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewingDocument, setViewingDocument] = useState<IHandbookDocument | null>(null);

  useEffect(() => {
    const workplaceId = Number(localStorage.getItem('workplace-id'));
    setIsLoading(true);
    setError(null);
    listHandbookDocuments(workplaceId, { isLatestVersion: true, throwOnError: true })
      .then((data) => {
        setDocuments(data);
      })
      .catch(() => {
        setDocuments([]);
        setError('Failed to load employee documents.');
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, []);

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const handleOpenDocument = (doc: IHandbookDocument) => {
    if (doc.filePath) {
      setViewingDocument(doc);
    }
  };

  const handleCopyUrl = async (doc: IHandbookDocument) => {
    const url = getHandbookDocumentUrl(doc.filePath);
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
      showToast('success', 'Document URL copied to clipboard');
    } catch {
      showToast('error', 'Failed to copy URL');
    }
  };

  const viewingDocumentUrl = getHandbookDocumentUrl(viewingDocument?.filePath);

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom sx={{ fontFamily: 'Gotham' }}>
            Employee Documents
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
            <CircularProgress size={32} sx={{ color: '#446388' }} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h5" gutterBottom sx={{ fontFamily: 'Gotham' }}>
          Employee Documents
        </Typography>

        {error && (
          <Typography color="error" sx={{ fontFamily: 'Gotham', mb: 2 }}>
            {error}
          </Typography>
        )}

        {!error && documents.length === 0 && (
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            minHeight={120}
            sx={{ color: '#888' }}
          >
            <PictureAsPdf sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
            <Typography sx={{ fontFamily: 'Gotham' }}>
              No employee documents available.
            </Typography>
          </Box>
        )}

        {!error && documents.length > 0 && (
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.875rem',
              fontFamily: 'Gotham',
            }}
          >
            <Box
              component="thead"
              sx={{ '& th': { textAlign: 'left', pb: 1, borderBottom: '2px solid #e0e0e0', color: '#446388', fontWeight: 600, pr: 2 } }}
            >
              <tr>
                <th>Document Name</th>
                <th style={{ width: 80 }}>Version</th>
                <th style={{ width: 160 }}>Last Updated</th>
                <th style={{ width: 100, textAlign: 'center' }}>Actions</th>
              </tr>
            </Box>
            <Box
              component="tbody"
              sx={{
                '& tr': { borderBottom: '1px solid #f0f0f0' },
                '& tr:hover': { backgroundColor: '#fafafa' },
                '& td': { py: 1.5, pr: 2, verticalAlign: 'middle' },
              }}
            >
              {documents.map((doc) => (
                <tr key={doc.id}>
                  <td>
                    <Box display="flex" alignItems="center" gap={1}>
                      <PictureAsPdf sx={{ color: '#d32f2f', fontSize: 20 }} />
                      <Typography sx={{ fontFamily: 'Gotham', fontWeight: 500 }}>
                        {doc.name}
                      </Typography>
                    </Box>
                  </td>
                  <td>
                    <Typography sx={{ fontFamily: 'Gotham', color: '#666' }}>
                      v{doc.version ?? 1}
                    </Typography>
                  </td>
                  <td>
                    <Typography sx={{ fontFamily: 'Gotham', color: '#666' }}>
                      {formatDate(doc.updatedAt || doc.createdAt)}
                    </Typography>
                  </td>
                  <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                    {doc.filePath && (
                      <>
                        <Tooltip title="View">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDocument(doc)}
                            aria-label={`View ${doc.name}`}
                            sx={{ color: '#446388' }}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Copy URL">
                          <IconButton
                            size="small"
                            onClick={() => handleCopyUrl(doc)}
                            aria-label={`Copy URL for ${doc.name}`}
                            sx={{ color: '#446388' }}
                          >
                            <ContentCopy fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </Box>
          </Box>
        )}
      </CardContent>

      <PdfViewerDialog
        open={!!viewingDocument}
        onClose={() => setViewingDocument(null)}
        pdfUrl={viewingDocumentUrl}
        title={viewingDocument?.name}
      />
    </Card>
  );
};
